import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useContext, useMemo, useRef, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { patchAccessory } from '@services/queries/onboarding/patchAccessory';

import { NavigationContext } from '@layout/navigation/navigation';
import { useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Line,
  Confirm,
  Checkbox,
  CheckContainer,
  CheckText,
  WarningContainer,
  Description,
  CheckTextDescription,
  CheckDivider,
  TextsRowFlex,
  TextsRowFlexItem,
  ErrorStatus,
  TextDescription,
  ToolTipText,
  Cancel,
} from './UnmatchedModel.styles';
import Labels from '../Labels/Labels';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { ErrorsFoundDialog } from '../ErrorsFoundDialog/ErrorsFoundDialog';
import { FieldErrorTypes } from '../OnboardingFile/OnboardingFile.query';

interface IUnmatchedModel {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
  field: FieldErrorTypes;
  closeFlow: () => void;
}

export const UnmatchedModel = ({
  open,
  onClose,
  group,
  field,
  closeFlow,
}: IUnmatchedModel) => {
  const portalDiv = document.getElementById('portal');

  const errorTooltipRef: any = useRef(null);

  const params = useParams<{ id: string }>();

  const { studioId, userEmail, userName } = useContext(NavigationContext);

  const queryClient = useQueryClient();

  const [selected, setSelected] = useState<string | null>(null);
  const [openDiscard, setOpenDiscard] = useState<boolean>(false);
  const [openIdentification, setOpenIdentification] = useState<boolean>(false);
  const [openInternal, setOpenInternal] = useState<boolean>(false);
  const [openHasError, setOpenHasError] = useState<boolean>(false);

  const fieldName = useMemo(() => {
    if (field === 'model') {
      return 'Model';
    }

    if (field === 'productName') {
      return 'Product Name';
    }

    return 'Manufacturer';
  }, [group, field]);

  const errorMessage = useMemo(() => {
    if (field === 'model') {
      return group.mpnError;
    }

    if (field === 'productName') {
      return group.productNameError;
    }

    return group.mfrError;
  }, [group, field]);

  const originalInput = useMemo(() => {
    if (field === 'model') {
      return group.model;
    }

    if (field === 'productName') {
      return group.productName;
    }

    return group.manufacturer;
  }, [group, field]);

  const accessoryMutation = useMutation(patchAccessory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      closeFlow();
    },
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <WarningContainer>
                <BlueIcon icon="error" size={16} color="#D14343" />
              </WarningContainer>
              <Title>Couldn’t match</Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <TextsRowFlex>
              <TextsRowFlexItem>
                <TextDescription>{originalInput}</TextDescription>
              </TextsRowFlexItem>

              <TextsRowFlexItem>
                <ToolTipText
                  ref={errorTooltipRef}
                  data-tip={`${fieldName} is either incorrect or does not exist.`}
                />
                <ReactTooltip effect="solid" globalEventOff="click" />
                <ErrorStatus
                  onMouseOver={() => ReactTooltip.show(errorTooltipRef.current)}
                  onMouseLeave={() => {
                    ReactTooltip.hide(errorTooltipRef.current);
                  }}
                >
                  <Labels text={errorMessage || 'Invalid'} type="error" />
                </ErrorStatus>
              </TextsRowFlexItem>
            </TextsRowFlex>

            <Description>
              Unfortunately, with the information shared we cannot make a{' '}
              {fieldName}. How would you like to proceed:
            </Description>

            <CheckContainer onClick={() => setSelected('inventory')}>
              <Checkbox active={selected === 'inventory'} />
              <CheckText>Add as Internal Product</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              This type of product is added immediatelly, but no additional
              product data can be included by Beam. However, you’ll be able to
              add your own information.
            </CheckTextDescription>

            <CheckDivider />

            <CheckContainer
              onClick={() => setSelected('identification_requested')}
            >
              <Checkbox active={selected === 'identification_requested'} />
              <CheckText>Identify Product</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              We will remove the Internal flag and this product will return to
              the list as a common product to be processed just like the others.
              If there are no more errors, this product will be moved directly
              to the <b>Identified</b> tab.
            </CheckTextDescription>

            <CheckDivider />

            <CheckContainer onClick={() => setSelected('discarded')}>
              <Checkbox active={selected === 'discarded'} />
              <CheckText>Discard it</CheckText>
            </CheckContainer>
            <CheckTextDescription>
              In case you don’t need this product, you can <b>discard</b> it
              now.
              <p>
                <i>
                  It’ll appear in the “Discarded” tab. You can always “undo” if
                  necessary.
                </i>
              </p>
            </CheckTextDescription>
            <Line>
              <Cancel type="button" onClick={onClose}>
                Cancel
              </Cancel>
              <Confirm
                type="button"
                disabled={!selected || accessoryMutation.isLoading}
                onClick={() => {
                  if (selected === 'inventory') {
                    setOpenInternal(true);
                  }
                  if (selected === 'discarded') {
                    setOpenDiscard(true);
                  }
                  if (selected === 'identification_requested') {
                    setOpenIdentification(true);
                  }
                }}
              >
                Confirm
              </Confirm>
            </Line>
          </Container>
          {openInternal && (
            <ConfirmDialog
              open={openInternal}
              onClose={() => setOpenInternal(false)}
              isLoading={accessoryMutation.isLoading}
              onConfirm={() => {
                if (selected) {
                  accessoryMutation.mutate({
                    groupId: group.id,
                    status: selected,
                    studioId,
                    fileId: params.id,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              showAsset
              isLarge
              group={group}
              title="Internal Product added"
              description="This type of product is added immediatelly, but no additional product data can be included by Beam. However, you’ll be able to add your own information."
            />
          )}
          {openIdentification && (
            <ConfirmDialog
              open={openIdentification}
              onClose={() => setOpenIdentification(false)}
              isLoading={accessoryMutation.isLoading}
              onConfirm={() => {
                if (selected) {
                  accessoryMutation.mutate({
                    groupId: group.id,
                    status: selected,
                    studioId,
                    fileId: params.id,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              title="Identification in process"
              description="The product information is being processed. If there are errors, you will see them in the “Unidentified” section. Otherwise, it will be moved to the “Identified” section."
            />
          )}
          {openDiscard && (
            <ConfirmDialog
              open={openDiscard}
              onClose={() => setOpenDiscard(false)}
              isLoading={accessoryMutation.isLoading}
              onConfirm={() => {
                if (selected) {
                  accessoryMutation.mutate({
                    groupId: group.id,
                    status: selected,
                    studioId,
                    fileId: params.id,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              title="Product Discarded"
              description="The product was discarded and can be found in the “Discarded” tab."
            />
          )}
          {openHasError && (
            <ErrorsFoundDialog
              open={openHasError}
              onClose={() => setOpenHasError(false)}
              group={group}
              onSkip={() => {
                accessoryMutation.mutate({
                  groupId: group.id,
                  status: 'inventory',
                  studioId,
                  fileId: params.id,
                  triggeredBy: userName,
                  triggeredByEmail: userEmail,
                });
              }}
              isLoading={accessoryMutation.isLoading}
              closeFlow={onClose}
            />
          )}
        </Overlay>,
        portalDiv,
      )
    : null;
};
